// src/redux/userSlice.js

import { createSlice } from '@reduxjs/toolkit';
import {jwtDecode} from 'jwt-decode';

// Define user roles as constants
export const USER_ROLES = {
  CLIENT_ADMIN: 'CLIENT_ADMIN',
  SUPERVISOR: 'SUPERVISOR'
};

const initialToken = localStorage.getItem('token');
let initialState = {
  token: initialToken || null,
  userType: null,
  isAuthenticated: !!initialToken,
  user: null,
  users: [],
  error: null,
};

// Decode JWT if token is in localStorage
if (initialToken) {
  try {
    const decoded = jwtDecode(initialToken);
    initialState.userType = decoded.userType;
    initialState.user = decoded.user; // Assuming 'user' is included in the token
  } catch (error) {
    console.error('Error decoding token:', error);
    // If token is invalid, remove it from localStorage
    localStorage.removeItem('token');
    initialState.token = null;
    initialState.isAuthenticated = false;
  }
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.isAuthenticated = true;

      // Decode JWT and set userType
      try {
        const decoded = jwtDecode(action.payload.token);
        state.userType = decoded.userType;
      } catch (error) {
        console.error('Error decoding token:', error);
        state.userType = null;
        state.isAuthenticated = false;
      }
    },

    loginFailure: (state, action) => {
      state.error = action.payload;
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
    },

    logout: (state) => {
      state.token = null;
      state.userType = null;
      state.isAuthenticated = false;
      state.user = null;
      state.createdUser = null;
      state.error = null;
      localStorage.removeItem('token'); // Explicitly remove token from localStorage
    },
    createUserSuccess: (state, action) => {
      state.createdUser = action.payload;
      state.error = null; // Clear any previous errors on success
    },
    createUserFailure: (state, action) => {
      state.error = action.payload; // Store error message when creation fails
    },
    fetchSubadminsSuccess: (state, action) => {
      state.subadmins = action.payload; // Store the fetched subadmins
      state.error = null; // Clear any error
    },
    fetchSubadminsFailure: (state, action) => {
      state.error = action.payload; // Store error message if fetching fails
    },
    fetchUsersSuccess: (state, action) => {
      state.users = action.payload; // Store the fetched users
      state.error = null; // Clear any error
    },
    fetchUsersFailure: (state, action) => {
      state.error = action.payload; // Store error message if fetching fails
    },
    editUserSuccess: (state, action) => {
      // Update user in users array
      const index = state.users.findIndex(user => user._id === action.payload._id);
      if (index !== -1) {
        state.users[index] = action.payload;
      }

      // If the edited user is the current user, update state.user
      if (state.user && state.user._id === action.payload._id) {
        state.user = action.payload;
      }

      state.error = null;
    },
    editUserFailure: (state, action) => {
      state.error = action.payload;
    },
    deleteUserSuccess: (state, action) => {
      // Remove user from users array
      state.users = state.users.filter(user => user._id !== action.payload);
      state.error = null;
    },
    deleteUserFailure: (state, action) => {
      state.error = action.payload;
    },

    // Add change password success/failure reducers
    changePasswordSuccess: (state) => {
      state.error = null;
    },
    changePasswordFailure: (state, action) => {
      state.error = action.payload;
    },

  },
});

export const { 
  loginSuccess, 
  loginFailure, 
  logout, 
  createUserSuccess, 
  createUserFailure, 
  fetchSubadminsSuccess, 
  fetchSubadminsFailure,
  fetchUsersSuccess, 
  fetchUsersFailure,  
  editUserSuccess,      
  editUserFailure,      
  changePasswordSuccess, 
  changePasswordFailure,
  deleteUserSuccess,
  deleteUserFailure,
} = userSlice.actions;

export default userSlice.reducer;
